import { useQueryClient, useMutation } from '@lumapps/base-api/react-query';
import { MEDIAS } from '@lumapps/medias/keys';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { useTranslate } from '@lumapps/translations';
import { GLOBAL } from '@lumapps/translations/keys';

import { documentsQueryKeys, moveDocument, type MoveDocumentParams } from '../api';
import { DocumentV1 } from '../types';

/**
 * Hook to move a document
 */
export const useMoveDocument = (document: DocumentV1) => {
    const queryClient = useQueryClient();
    const { success, error: errorNotification } = useNotification();
    const { translateAndReplace, translateObject, translateKey } = useTranslate();

    const mediaName = document?.name ? `"${translateObject(document.name)}"` : `"${translateKey(GLOBAL.UNKNOWN)}"`;

    return useMutation({
        mutationFn: (params: MoveDocumentParams) => moveDocument(params),
        onError: () => {
            /** Display an error notification */
            errorNotification({ translate: GLOBAL.ERROR });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: documentsQueryKeys.all() });

            /** Display a success notification */
            success({
                content: document.isFolder
                    ? translateAndReplace(MEDIAS.FOLDER_MOVED, {
                          FOLDER_NAME: mediaName,
                      })
                    : translateAndReplace(MEDIAS.FILE_MOVED, {
                          FILE_NAME: mediaName,
                      }),
            });
        },
    });
};
